import { COUNTRIES_MAP } from '@/const/countries.const';

export type IReservationInstant = Date | string | number;
export const RESERVATION_FALLBACK_ZONE = 'America/Argentina/Buenos_Aires';
const partsFormatters = new Map<string, Intl.DateTimeFormat>();

function instant(value: IReservationInstant): Date {
  const result = value instanceof Date ? new Date(value) : new Date(value);
  if (!Number.isFinite(result.getTime())) throw new RangeError('Invalid date');
  return result;
}

function localParts(value: IReservationInstant, timeZone: string) {
  let formatter = partsFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    partsFormatters.set(timeZone, formatter);
  }
  const parts = formatter.formatToParts(instant(value));
  const number = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return {
    year: number('year'),
    month: number('month'),
    day: number('day'),
    hour: number('hour'),
    minute: number('minute'),
    second: number('second'),
  };
}

function parseDateKey(value: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
    throw new RangeError('Invalid calendar date');
  const date = new Date(`${value}T00:00:00.000Z`);
  if (
    !Number.isFinite(date.getTime()) ||
    date.toISOString().slice(0, 10) !== value
  )
    throw new RangeError('Invalid calendar date');
  return date;
}

export function toDateKey(
  value: IReservationInstant,
  timeZone: string
): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value))
    return parseDateKey(value).toISOString().slice(0, 10);
  const { year, month, day } = localParts(value, timeZone);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
    2,
    '0'
  )}`;
}

export function addDays(date: string, days: number): string {
  if (!Number.isInteger(days))
    throw new RangeError('Expected whole calendar days');
  const value = parseDateKey(date);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function formatTime(
  value: IReservationInstant,
  timeZone: string,
  locale = 'es'
): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(instant(value));
}

export function formatDateLong(
  value: IReservationInstant,
  timeZone: string,
  locale = 'es'
): string {
  const date =
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? fromLocalDateTime(value, '12:00', timeZone)
      : value;
  const formatted = new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(instant(date));
  return formatted.charAt(0).toLocaleUpperCase(locale) + formatted.slice(1);
}

/** Short weekday, day and month: "sáb, 4 oct" / "Sat, Oct 4". */
export function formatDayShort(
  value: IReservationInstant,
  timeZone: string,
  locale = 'es'
): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(instant(value));
}

export function getReservationTimeZone(
  location: { tz?: string | null } | null | undefined,
  country?: string | null
): string {
  return (
    location?.tz ??
    COUNTRIES_MAP.get(country ?? '')?.tz ??
    RESERVATION_FALLBACK_ZONE
  );
}

export function reservationCutoverHour(value = 4): number {
  return (
    Math.min(8, Math.max(0, Number.isFinite(value) ? Math.trunc(value) : 4)) ||
    4
  );
}

export function defaultBoardDate(
  now: IReservationInstant,
  timeZone: string,
  businessDayStartHour = 4
): string {
  const day = toDateKey(now, timeZone);
  return localParts(now, timeZone).hour <
    reservationCutoverHour(businessDayStartHour)
    ? addDays(day, -1)
    : day;
}

export function timeToMinutes(time: string): number {
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);
  if (
    !match ||
    Number(match[1]) > 23 ||
    Number(match[2]) > 59 ||
    Number(match[3] ?? 0) > 59
  )
    throw new RangeError('Invalid local time');
  return Number(match[1]) * 60 + Number(match[2]);
}

/** Resolve a wall time using the branch zone, choosing the first repeated time
 * and moving nonexistent times forward by the DST gap, matching the API. */
export function fromLocalDateTime(
  date: string,
  time: string,
  timeZone: string
): Date {
  const midnight = parseDateKey(date).getTime();
  const wanted = midnight + timeToMinutes(time) * 60000;
  const offsets = new Set<number>();
  for (let hours = -48; hours <= 48; hours += 6) {
    const probe = wanted + hours * 3600000;
    const part = localParts(probe, timeZone);
    offsets.add(
      Date.UTC(
        part.year,
        part.month - 1,
        part.day,
        part.hour,
        part.minute,
        part.second
      ) - probe
    );
  }
  const candidates = [...offsets]
    .map((offset) => wanted - offset)
    .sort((a, b) => a - b);
  const wall = (candidate: number) => {
    const part = localParts(candidate, timeZone);
    return Date.UTC(
      part.year,
      part.month - 1,
      part.day,
      part.hour,
      part.minute
    );
  };
  const exact = candidates.find((candidate) => wall(candidate) === wanted);
  if (exact !== undefined) return new Date(exact);
  const forward = candidates
    .filter((candidate) => wall(candidate) > wanted)
    .sort((a, b) => wall(a) - wall(b) || a - b)[0];
  if (forward === undefined)
    throw new RangeError('Unable to resolve local time');
  return new Date(forward);
}

export function shiftWindow(
  date: string,
  from: string,
  to: string,
  timeZone: string
): { startsAt: Date; endsAt: Date } {
  const startsAt = fromLocalDateTime(date, from, timeZone);
  const endDate =
    timeToMinutes(to) < timeToMinutes(from) ? addDays(date, 1) : date;
  return { startsAt, endsAt: fromLocalDateTime(endDate, to, timeZone) };
}
